import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  Minus,
  BarChart3,
  PieChart,
  Zap,
  Shield,
  Clock,
  Gift,
  Star,
  Eye,
  EyeOff,
  Repeat,
  Send,
  QrCode,
  Copy,
  ExternalLink,
  X,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  RefreshCw,
  Lock,
  Unlock,
  Image,
  Search,
  Settings,
  Bell,
  MoreHorizontal,
  Scan,
  ArrowRight,
  CheckCircle2,
  History,
  Layers,
  Coins,
  Sparkles
} from 'lucide-react'
import { CryptoAPI, NFTAPI, extractArray } from '../services/api'

// Swift Gold Fiduciary Theme - Matching bank design system
const colors = {
  primary: 'rgb(37, 99, 235)', // blue-600
  primaryHover: 'rgb(29, 78, 216)', // blue-700
  success: 'rgb(16, 185, 129)', // emerald-500
  danger: 'rgb(239, 68, 68)', // red-500
  warning: 'rgb(245, 158, 11)', // amber-500
  background: 'rgb(255, 255, 255)', // white
  card: 'rgb(255, 255, 255)', // white
  cardHover: 'rgb(248, 250, 252)', // slate-50
  border: 'rgb(226, 232, 240)', // slate-200
  text: 'rgb(15, 23, 42)', // slate-900
  textSecondary: 'rgb(100, 116, 139)', // slate-500
  textMuted: 'rgb(148, 163, 184)' // slate-400
}

export default function CryptoWallet() {
  const { t, i18n } = useTranslation(['crypto', 'common'])
  const [showBalances, setShowBalances] = useState(true)
  const [activeTab, setActiveTab] = useState('tokens')
  const [selectedCrypto, setSelectedCrypto] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data states
  const [wallet, setWallet] = useState(null)
  const [holdings, setHoldings] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [stakingPools, setStakingPools] = useState([])
  const [userStakes, setUserStakes] = useState([])
  const [defiPools, setDefiPools] = useState([])
  const [userDefiPositions, setUserDefiPositions] = useState([])
  const [nfts, setNfts] = useState([])
  const [networks, setNetworks] = useState([])
  const [depositAddresses, setDepositAddresses] = useState([])
  const [deposits, setDeposits] = useState([])
  const [withdrawals, setWithdrawals] = useState([])
  const [transactions, setTransactions] = useState([])

  // Modal states
  const [showBuyModal, setShowBuyModal] = useState(false)
  const [showSellModal, setShowSellModal] = useState(false)
  const [showStakeModal, setShowStakeModal] = useState(false)
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [showSwapModal, setShowSwapModal] = useState(false)
  const [showCryptoDetail, setShowCryptoDetail] = useState(false)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const [
        walletsRes,
        currenciesRes,
        portfolioRes,
        stakingPoolsRes,
        userStakesRes,
        defiPoolsRes,
        userDefiRes,
        networksRes
      ] = await Promise.all([
        CryptoAPI.getWallets(),
        CryptoAPI.getCurrencies(),
        CryptoAPI.getPortfolio(),
        CryptoAPI.getStakingPools(),
        CryptoAPI.getMyStakes(),
        CryptoAPI.getDefiPools(),
        CryptoAPI.getMyDefiPositions(),
        CryptoAPI.getNetworks()
      ])

      if (walletsRes.success) {
        let wallets = []
        if (Array.isArray(walletsRes.data)) {
          wallets = walletsRes.data
        } else if (walletsRes.data?.wallets) {
          wallets = walletsRes.data.wallets
        } else if (walletsRes.data?.id) {
          wallets = [walletsRes.data]
        }

        if (wallets.length === 0) {
          try {
            const createRes = await CryptoAPI.createWallet('My Crypto Wallet', null)
            if (createRes.success && createRes.data) {
              wallets = [createRes.data]
            }
          } catch (err) {
            console.error('Failed to create wallet:', err)
          }
        }

        setWallet(wallets[0] || null)
      }

      if (currenciesRes.success) {
        setCurrencies(extractArray(currenciesRes, 'currencies') || extractArray(currenciesRes, 'cryptocurrencies') || [])
      }

      if (portfolioRes.success && portfolioRes.data) {
        setHoldings(extractArray(portfolioRes, 'holdings') || [])
      }

      if (stakingPoolsRes.success) {
        setStakingPools(extractArray(stakingPoolsRes, 'pools') || [])
      }

      if (userStakesRes.success) {
        setUserStakes(extractArray(userStakesRes, 'stakes') || [])
      }

      if (defiPoolsRes.success) {
        setDefiPools(extractArray(defiPoolsRes, 'pools') || [])
      }

      if (userDefiRes.success) {
        setUserDefiPositions(extractArray(userDefiRes, 'positions') || [])
      }

      if (networksRes.success) {
        setNetworks(extractArray(networksRes, 'networks') || [])
      }

      try {
        const [addressesRes, depositsRes, withdrawalsRes, transactionsRes, nftsRes] = await Promise.all([
          CryptoAPI.getDepositAddresses(),
          CryptoAPI.getDeposits(),
          CryptoAPI.getWithdrawals(),
          CryptoAPI.getTransactions({ limit: 50 }),
          NFTAPI.getMyNFTs()
        ])

        if (addressesRes.success) setDepositAddresses(extractArray(addressesRes, 'addresses') || [])
        if (depositsRes.success) setDeposits(extractArray(depositsRes, 'deposits') || [])
        if (withdrawalsRes.success) setWithdrawals(extractArray(withdrawalsRes, 'withdrawals') || [])
        if (transactionsRes.success) setTransactions(extractArray(transactionsRes, 'transactions') || [])
        if (nftsRes.success) setNfts(extractArray(nftsRes, 'nfts') || [])
      } catch (e) {
        console.warn('Some additional data failed to load:', e)
      }

    } catch (err) {
      console.error('Error fetching crypto data:', err)
      setError('Failed to load wallet data')
    } finally {
      setLoading(false)
    }
  }

  // Combine holdings with currency data for display
  const portfolioData = holdings.map(h => {
    const currency = currencies.find(c => c.id === h.crypto_id || c.symbol === h.symbol)
    return {
      ...h,
      ...currency,
      holdings: parseFloat(h.balance) || 0,
      value: (parseFloat(h.balance) || 0) * (parseFloat(currency?.current_price) || 0),
      change24h: parseFloat(currency?.price_change_percentage_24h) || 0
    }
  })

  const totalPortfolioValue = portfolioData.reduce((sum, crypto) => sum + (crypto.value || 0), 0)
  const totalChange24h = portfolioData.reduce((sum, crypto) => sum + ((crypto.value || 0) * (crypto.change24h || 0) / 100), 0)
  const totalChangePercentage = totalPortfolioValue > 0 ? (totalChange24h / totalPortfolioValue) * 100 : 0

  // Merge currencies with holdings - show all currencies with their balance (0 if no holdings)
  const allCurrencies = currencies.map(currency => {
    const holding = portfolioData.find(p => p.id === currency.id || p.crypto_id === currency.id)
    return {
      ...currency,
      holdings: holding?.holdings || 0,
      value: holding?.value || 0,
      change24h: holding?.change24h || parseFloat(currency.price_change_percentage_24h) || 0,
      current_price: currency.current_price
    }
  })

  const handleBuyCrypto = async (cryptoId, amountUsd) => {
    if (!wallet) return
    setProcessing(true)
    try {
      const res = await CryptoAPI.buy(wallet.id, cryptoId, amountUsd)
      if (res.success) {
        fetchData()
        setShowBuyModal(false)
        setSelectedCrypto(null)
      }
    } catch (err) {
      console.error('Buy failed:', err)
    } finally {
      setProcessing(false)
    }
  }

  const handleSellCrypto = async (cryptoId, quantity) => {
    if (!wallet) return
    setProcessing(true)
    try {
      const res = await CryptoAPI.sell(wallet.id, cryptoId, quantity)
      if (res.success) {
        fetchData()
        setShowSellModal(false)
        setSelectedCrypto(null)
      }
    } catch (err) {
      console.error('Sell failed:', err)
    } finally {
      setProcessing(false)
    }
  }

  const handleGenerateAddress = async (cryptoId, networkId) => {
    if (!wallet) return null
    setProcessing(true)
    try {
      const res = await CryptoAPI.generateDepositAddress(wallet.id, cryptoId, networkId)
      if (res.success) {
        const addressesRes = await CryptoAPI.getDepositAddresses()
        if (addressesRes.success) {
          setDepositAddresses(extractArray(addressesRes, 'addresses') || [])
        }
        return res.data?.address || res.data
      }
    } catch (err) {
      console.error('Generate address failed:', err)
    } finally {
      setProcessing(false)
    }
    return null
  }

  const handleWithdraw = async (cryptoId, networkId, amount, destinationAddress, memo) => {
    if (!wallet) return
    setProcessing(true)
    try {
      const res = await CryptoAPI.createWithdrawal(wallet.id, cryptoId, networkId, amount, destinationAddress, memo)
      if (res.success) {
        fetchData()
        setShowWithdrawModal(false)
      }
      return res
    } catch (err) {
      console.error('Withdrawal failed:', err)
      return { success: false, message: err.message }
    } finally {
      setProcessing(false)
    }
  }

  // Get crypto icon color based on symbol
  const getCryptoColor = (symbol) => {
    const colorMap = {
      'BTC': 'from-orange-400 to-orange-600',
      'ETH': 'from-blue-400 to-indigo-600',
      'SOL': 'from-purple-400 to-purple-600',
      'BNB': 'from-yellow-400 to-yellow-600',
      'XRP': 'from-gray-400 to-gray-600',
      'ADA': 'from-blue-400 to-blue-600',
      'DOGE': 'from-yellow-300 to-yellow-500',
      'MATIC': 'from-purple-500 to-purple-700',
      'DOT': 'from-pink-400 to-pink-600',
      'LINK': 'from-blue-500 to-blue-700',
      'AVAX': 'from-red-400 to-red-600',
      'UNI': 'from-pink-400 to-pink-500',
      'USDT': 'from-emerald-400 to-emerald-600',
      'USDC': 'from-blue-400 to-blue-500'
    }
    return colorMap[symbol?.toUpperCase()] || 'from-gray-400 to-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Wallet className="w-8 h-8 text-white" />
          </div>
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-slate-500">{t('common:loading')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-900 font-medium mb-2">{error}</p>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl mx-auto transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common:tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Swift Gold Fiduciary Style */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{t('title')}</h1>
                <p className="text-xs md:text-sm text-slate-500 hidden sm:block">{t('subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchData}
                className="p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className="w-5 h-5 text-slate-600" />
              </button>
              <button className="p-2.5 hover:bg-slate-100 rounded-lg transition-colors">
                <Scan className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Main Grid Layout for PC */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - Balance Card & Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
              <div className="relative">
                <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-slate-400 text-sm font-medium">{t('wallet.totalValue')}</p>
                      <button
                        onClick={() => setShowBalances(!showBalances)}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        {showBalances ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                      </button>
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
                      {showBalances ? (
                        `$${totalPortfolioValue.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      ) : (
                        '••••••'
                      )}
                    </h2>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className={`inline-flex items-center gap-1.5 ${
                      totalChangePercentage >= 0
                        ? 'bg-emerald-500/20 text-emerald-300'
                        : 'bg-red-500/20 text-red-300'
                    } px-3 py-1.5 rounded-full text-sm font-semibold`}>
                      {totalChangePercentage >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {totalChangePercentage >= 0 ? '+' : ''}{totalChangePercentage.toFixed(2)}%
                    </div>
                    <p className="text-slate-400 text-xs mt-2">{t('assets.change24h')}</p>
                  </div>
                </div>

                {/* Quick Action Buttons */}
                <div className="grid grid-cols-4 gap-2 md:gap-3">
                  <button
                    onClick={() => setShowDepositModal(true)}
                    className="flex flex-col items-center gap-2 p-3 md:p-4 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <ArrowDownLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <span className="text-white text-xs md:text-sm font-medium">{t('actions.receive')}</span>
                  </button>
                  <button
                    onClick={() => setShowWithdrawModal(true)}
                    className="flex flex-col items-center gap-2 p-3 md:p-4 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <span className="text-white text-xs md:text-sm font-medium">{t('actions.send')}</span>
                  </button>
                  <button
                    onClick={() => setShowBuyModal(true)}
                    className="flex flex-col items-center gap-2 p-3 md:p-4 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Plus className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <span className="text-white text-xs md:text-sm font-medium">{t('actions.buy')}</span>
                  </button>
                  <button
                    onClick={() => setShowSwapModal(true)}
                    className="flex flex-col items-center gap-2 p-3 md:p-4 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Repeat className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <span className="text-white text-xs md:text-sm font-medium">{t('actions.swap')}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex border-b border-slate-200">
                {[
                  { id: 'tokens', label: t('wallet.assets'), icon: Coins },
                  { id: 'nfts', label: t('tabs.nfts'), icon: Image },
                  { id: 'staking', label: t('tabs.staking'), icon: Sparkles },
                  { id: 'activity', label: t('tabs.transactions'), icon: History }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 md:py-4 text-sm font-medium transition-all border-b-2 ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Tokens Tab Content */}
              <div className="p-4 md:p-6">
        {activeTab === 'tokens' && (
          <div className="space-y-3">
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search tokens"
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* Show all currencies - merge holdings with available currencies */}
            {currencies.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No cryptocurrencies available</h3>
                <p className="text-slate-500 mb-6">Check back later for available tokens</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
              {allCurrencies.map((crypto, index) => (
                <motion.div
                  key={crypto.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => { setSelectedCrypto(crypto); setShowCryptoDetail(true); }}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer transition-all -mx-4 md:-mx-6 px-4 md:px-6"
                >
                    <div className="flex items-center gap-4">
                      <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${getCryptoColor(crypto.symbol)} flex items-center justify-center shadow-sm`}>
                        <span className="font-bold text-white text-base">{crypto.symbol?.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{crypto.name || crypto.symbol}</h3>
                        <p className="text-sm text-slate-500">
                          {showBalances
                            ? `${(crypto.holdings || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${crypto.symbol}`
                            : '•••••'
                          }
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {showBalances
                          ? `$${(crypto.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '••••'
                        }
                      </p>
                      <div className={`flex items-center justify-end gap-1 text-sm ${
                        (crypto.change24h || parseFloat(crypto.price_change_percentage_24h || 0)) >= 0 ? 'text-emerald-600' : 'text-red-500'
                      }`}>
                        {(crypto.change24h || parseFloat(crypto.price_change_percentage_24h || 0)) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {(crypto.change24h || parseFloat(crypto.price_change_percentage_24h || 0)) >= 0 ? '+' : ''}{(crypto.change24h || parseFloat(crypto.price_change_percentage_24h || 0)).toFixed(2)}%
                      </div>
                    </div>
                </motion.div>
              ))}
              </div>
            )}
          </div>
        )}

        {/* NFTs Tab */}
        {activeTab === 'nfts' && (
          <div>
            {nfts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Image className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No NFTs yet</h3>
                <p className="text-slate-500 mb-6">Explore and collect unique digital assets</p>
                <button className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all">
                  Explore NFTs
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {nfts.map((nft) => (
                  <motion.div
                    key={nft.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer"
                  >
                    <div className="aspect-square bg-slate-100">
                      {nft.image_url ? (
                        <img src={nft.image_url} alt={nft.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-slate-500 truncate">{nft.collection_name}</p>
                      <h3 className="font-medium text-slate-900 truncate">{nft.name}</h3>
                      {nft.price && (
                        <p className="text-sm font-medium text-blue-600 mt-1">{nft.price} {nft.currency || 'ETH'}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Staking/Earn Tab */}
        {activeTab === 'staking' && (
          <div className="space-y-6">
            {/* User's Active Stakes */}
            {userStakes.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Your Stakes</h3>
                <div className="space-y-3">
                  {userStakes.map((stake) => (
                    <div key={stake.id} className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                            <Lock className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{stake.pool_name}</p>
                            <p className="text-xs text-slate-500">{stake.symbol}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                          stake.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {stake.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Staked</p>
                          <p className="font-medium text-slate-900">{parseFloat(stake.amount_staked).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Rewards</p>
                          <p className="font-medium text-emerald-600">+{parseFloat(stake.rewards_earned || 0).toFixed(6)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">APY</p>
                          <p className="font-medium text-slate-900">{stake.apy_rate}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Staking Pools */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Earn Rewards</h3>
              {stakingPools.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 border border-slate-200 rounded-xl">
                  <Sparkles className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500">No staking pools available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stakingPools.map((pool) => (
                    <motion.div
                      key={pool.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm rounded-xl p-4 cursor-pointer transition-all"
                      onClick={() => { setSelectedCrypto(pool); setShowStakeModal(true); }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${getCryptoColor(pool.symbol)} flex items-center justify-center`}>
                            <span className="font-bold text-white">{pool.symbol?.charAt(0)}</span>
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{pool.name}</h4>
                            <p className="text-sm text-slate-500">{pool.lock_period_days > 0 ? `${pool.lock_period_days} days lock` : 'Flexible'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">{pool.apy_rate}%</p>
                          <p className="text-xs text-slate-500">APY</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* DeFi Section */}
            {defiPools.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-4">DeFi</h3>
                <div className="space-y-3">
                  {defiPools.map((pool) => (
                    <div
                      key={pool.id}
                      className="bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm rounded-xl p-4 cursor-pointer transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                            <Zap className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{pool.name}</h4>
                            <p className="text-sm text-slate-500">{pool.protocol}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">{pool.apy_rate}%</p>
                          <p className="text-xs text-slate-500">TVL: ${parseFloat(pool.tvl || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div>
            {transactions.length === 0 && deposits.length === 0 && withdrawals.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <History className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No activity yet</h3>
                <p className="text-slate-500">Your transactions will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {[
                  ...deposits.map(d => ({ ...d, type: 'deposit', date: d.detected_at || d.created_at })),
                  ...withdrawals.map(w => ({ ...w, type: 'withdrawal', date: w.created_at })),
                  ...transactions.map(t => ({ ...t, date: t.created_at }))
                ]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((tx, index) => (
                    <motion.div
                      key={`${tx.type}-${tx.id}-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors -mx-4 md:-mx-6 px-4 md:px-6"
                    >
                        <div className="flex items-center gap-4">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                            tx.type === 'deposit' ? 'bg-emerald-50' :
                            tx.type === 'withdrawal' ? 'bg-red-50' :
                            tx.type === 'buy' ? 'bg-blue-50' :
                            tx.type === 'sell' ? 'bg-amber-50' :
                            'bg-slate-100'
                          }`}>
                            {tx.type === 'deposit' && <ArrowDownLeft className="w-5 h-5 text-emerald-600" />}
                            {tx.type === 'withdrawal' && <ArrowUpRight className="w-5 h-5 text-red-500" />}
                            {tx.type === 'buy' && <Plus className="w-5 h-5 text-blue-600" />}
                            {tx.type === 'sell' && <Minus className="w-5 h-5 text-amber-600" />}
                            {tx.type === 'swap' && <Repeat className="w-5 h-5 text-slate-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 capitalize">{tx.type}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(tx.date).toLocaleDateString()} {new Date(tx.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            tx.type === 'deposit' || tx.type === 'buy' ? 'text-emerald-600' : 'text-slate-900'
                          }`}>
                            {tx.type === 'deposit' || tx.type === 'buy' ? '+' : '-'}
                            {parseFloat(tx.amount || 0).toFixed(6)} {tx.symbol || tx.crypto_symbol}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            tx.status === 'completed' || tx.status === 'confirmed' || tx.status === 'credited'
                              ? 'bg-emerald-50 text-emerald-600'
                              : tx.status === 'pending' || tx.status === 'processing'
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-red-50 text-red-500'
                          }`}>
                            {tx.status}
                          </span>
                        </div>
                    </motion.div>
                  ))
                }
              </div>
            )}
          </div>
        )}
              </div>
            </div>
          </div>

          {/* Right Column - Quick Info Cards */}
          <div className="space-y-6">
            {/* Portfolio Summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Portfolio Summary</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Assets</span>
                  <span className="text-sm font-medium text-slate-900">{portfolioData.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Total Value</span>
                  <span className="text-sm font-medium text-slate-900">
                    {showBalances ? `$${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '••••••'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">24h Change</span>
                  <span className={`text-sm font-medium ${totalChangePercentage >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {totalChangePercentage >= 0 ? '+' : ''}{totalChangePercentage.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Top Holdings */}
            {portfolioData.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Top Holdings</h3>
                <div className="space-y-3">
                  {portfolioData.slice(0, 3).map((crypto, index) => (
                    <div key={crypto.id || index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getCryptoColor(crypto.symbol)} flex items-center justify-center`}>
                          <span className="font-bold text-white text-xs">{crypto.symbol?.charAt(0)}</span>
                        </div>
                        <span className="text-sm font-medium text-slate-900">{crypto.symbol}</span>
                      </div>
                      <span className="text-sm text-slate-600">
                        {showBalances ? `$${crypto.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '••••'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
              <h4 className="text-sm font-semibold mb-3">Learn More</h4>
              <p className="text-xs text-white/80 mb-4 leading-relaxed">
                Discover how to maximize your crypto portfolio with staking, DeFi, and more.
              </p>
              <button className="w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors">
                Explore Features
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Crypto Detail Modal */}
      <AnimatePresence>
        {showCryptoDetail && selectedCrypto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
            onClick={() => { setShowCryptoDetail(false); setSelectedCrypto(null); }}
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full md:w-[480px] bg-white rounded-t-2xl md:rounded-2xl max-h-[85vh] overflow-y-auto shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Handle - Mobile only */}
              <div className="flex justify-center pt-3 pb-2 md:hidden">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${getCryptoColor(selectedCrypto.symbol)} flex items-center justify-center shadow-md`}>
                    <span className="font-bold text-white text-xl">{selectedCrypto.symbol?.charAt(0)}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedCrypto.name}</h2>
                    <p className="text-slate-500">{selectedCrypto.symbol}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
                  <p className="text-slate-500 text-sm mb-1">Current Price</p>
                  <div className="flex items-end gap-3">
                    <span className="text-2xl font-bold text-slate-900">
                      ${parseFloat(selectedCrypto.current_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: selectedCrypto.current_price < 1 ? 6 : 2 })}
                    </span>
                    <span className={`text-base font-medium ${
                      selectedCrypto.change24h >= 0 ? 'text-emerald-600' : 'text-red-500'
                    }`}>
                      {selectedCrypto.change24h >= 0 ? '+' : ''}{selectedCrypto.change24h?.toFixed(2)}%
                    </span>
                  </div>
                </div>

                {/* Holdings */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <p className="text-slate-500 text-sm mb-1">Your Balance</p>
                    <p className="text-lg font-bold text-slate-900">
                      {showBalances
                        ? selectedCrypto.holdings?.toLocaleString(undefined, { maximumFractionDigits: 8 })
                        : '•••••'
                      }
                    </p>
                    <p className="text-slate-500 text-sm">{selectedCrypto.symbol}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <p className="text-slate-500 text-sm mb-1">Value</p>
                    <p className="text-lg font-bold text-slate-900">
                      {showBalances
                        ? `$${selectedCrypto.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '••••'
                      }
                    </p>
                    <p className="text-slate-500 text-sm">USD</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => { setShowCryptoDetail(false); setShowDepositModal(true); }}
                    className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <ArrowDownLeft className="w-4 h-4" />
                    Receive
                  </button>
                  <button
                    onClick={() => { setShowCryptoDetail(false); setShowWithdrawModal(true); }}
                    className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                    Send
                  </button>
                  <button
                    onClick={() => { setShowCryptoDetail(false); setShowSwapModal(true); }}
                    className="py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <Repeat className="w-4 h-4" />
                    Swap
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <button
                    onClick={() => { setShowCryptoDetail(false); setShowBuyModal(true); }}
                    className="py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all"
                  >
                    Buy
                  </button>
                  <button
                    onClick={() => { setShowCryptoDetail(false); setShowSellModal(true); }}
                    className="py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all"
                  >
                    Sell
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buy Modal */}
      <AnimatePresence>
        {showBuyModal && (
          <BuySellModal
            type="buy"
            crypto={selectedCrypto}
            currencies={currencies}
            onClose={() => { setShowBuyModal(false); setSelectedCrypto(null); }}
            onSubmit={handleBuyCrypto}
            processing={processing}
            getCryptoColor={getCryptoColor}
          />
        )}
      </AnimatePresence>

      {/* Sell Modal */}
      <AnimatePresence>
        {showSellModal && (
          <BuySellModal
            type="sell"
            crypto={selectedCrypto}
            currencies={portfolioData}
            onClose={() => { setShowSellModal(false); setSelectedCrypto(null); }}
            onSubmit={handleSellCrypto}
            processing={processing}
            getCryptoColor={getCryptoColor}
          />
        )}
      </AnimatePresence>

      {/* Deposit Modal */}
      <AnimatePresence>
        {showDepositModal && (
          <DepositModal
            currencies={currencies}
            initialCrypto={selectedCrypto}
            depositAddresses={depositAddresses}
            onGenerateAddress={handleGenerateAddress}
            onClose={() => { setShowDepositModal(false); setSelectedCrypto(null); }}
            processing={processing}
            getCryptoColor={getCryptoColor}
          />
        )}
      </AnimatePresence>

      {/* Withdraw Modal */}
      <AnimatePresence>
        {showWithdrawModal && (
          <WithdrawModal
            currencies={currencies}
            holdings={portfolioData}
            onWithdraw={handleWithdraw}
            onClose={() => setShowWithdrawModal(false)}
            processing={processing}
            getCryptoColor={getCryptoColor}
          />
        )}
      </AnimatePresence>

      {/* Swap Modal */}
      <AnimatePresence>
        {showSwapModal && (
          <SwapModal
            currencies={currencies}
            holdings={portfolioData}
            onClose={() => setShowSwapModal(false)}
            getCryptoColor={getCryptoColor}
          />
        )}
      </AnimatePresence>

      {/* Stake Modal */}
      <AnimatePresence>
        {showStakeModal && selectedCrypto && (
          <StakeModal
            pool={selectedCrypto}
            onClose={() => { setShowStakeModal(false); setSelectedCrypto(null); }}
            processing={processing}
            getCryptoColor={getCryptoColor}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Buy/Sell Modal - Swift Gold Fiduciary Style
function BuySellModal({ type, crypto, currencies, onClose, onSubmit, processing, getCryptoColor }) {
  const [selectedCrypto, setSelectedCrypto] = useState(crypto?.id || '')
  const [amount, setAmount] = useState('')
  const [showCryptoList, setShowCryptoList] = useState(false)

  const selectedCurrency = currencies.find(c => c.id == selectedCrypto)

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(selectedCrypto, parseFloat(amount))
  }

  const quickAmounts = type === 'buy' ? [100, 250, 500, 1000] : ['25%', '50%', '75%', 'MAX']

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full md:w-[480px] bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              {type === 'buy' ? 'Buy Crypto' : 'Sell Crypto'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Crypto Selector */}
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">Select Token</label>
              <button
                type="button"
                onClick={() => setShowCryptoList(!showCryptoList)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
              >
                {selectedCurrency ? (
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getCryptoColor(selectedCurrency.symbol)} flex items-center justify-center`}>
                      <span className="font-bold text-white">{selectedCurrency.symbol?.charAt(0)}</span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{selectedCurrency.symbol}</p>
                      <p className="text-sm text-slate-500">{selectedCurrency.name}</p>
                    </div>
                  </div>
                ) : (
                  <span className="text-slate-500">Select a token</span>
                )}
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showCryptoList ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showCryptoList && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2 bg-white border border-slate-200 rounded-xl overflow-hidden max-h-60 overflow-y-auto shadow-lg"
                  >
                    {currencies.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setSelectedCrypto(c.id); setShowCryptoList(false); }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getCryptoColor(c.symbol)} flex items-center justify-center`}>
                          <span className="font-bold text-white text-sm">{c.symbol?.charAt(0)}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-900">{c.symbol}</p>
                          <p className="text-xs text-slate-500">{c.name}</p>
                        </div>
                        {selectedCrypto == c.id && (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 ml-auto" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">
                {type === 'buy' ? 'Amount (USD)' : 'Quantity'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-xl font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder={type === 'buy' ? '0.00' : '0.00000000'}
                  required
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                  {type === 'buy' ? 'USD' : selectedCurrency?.symbol || ''}
                </div>
              </div>

              {/* Quick Amount Buttons */}
              <div className="flex gap-2 mt-3">
                {quickAmounts.map((amt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      if (typeof amt === 'number') {
                        setAmount(amt.toString())
                      } else if (amt === 'MAX') {
                        setAmount(selectedCurrency?.holdings?.toString() || '0')
                      } else {
                        const pct = parseInt(amt) / 100
                        setAmount((parseFloat(selectedCurrency?.holdings || 0) * pct).toString())
                      }
                    }}
                    className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-600 font-medium transition-colors"
                  >
                    {typeof amt === 'number' ? `$${amt}` : amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={processing || !selectedCrypto || !amount}
              className={`w-full py-4 rounded-xl font-medium text-white transition-all disabled:opacity-50 ${
                type === 'buy' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {processing ? (
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              ) : (
                type === 'buy' ? 'Buy Now' : 'Sell Now'
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Deposit Modal - Swift Gold Fiduciary Style
function DepositModal({ currencies, initialCrypto, depositAddresses, onGenerateAddress, onClose, processing, getCryptoColor }) {
  const [selectedCryptoId, setSelectedCryptoId] = useState(initialCrypto?.id || '')
  const [selectedNetwork, setSelectedNetwork] = useState('')
  const [generatedAddress, setGeneratedAddress] = useState(null)
  const [copied, setCopied] = useState(false)
  const [networks, setNetworks] = useState([])
  const [loadingNetworks, setLoadingNetworks] = useState(false)
  // Start at step 2 if crypto was preselected, otherwise step 1
  const [step, setStep] = useState(initialCrypto?.id ? 2 : 1)

  const selectedCurrency = currencies.find(c => c.id == selectedCryptoId) || initialCrypto
  const existingAddress = depositAddresses.find(a => a.network_id == selectedNetwork && a.crypto_id == selectedCryptoId)
  const displayAddress = existingAddress?.address || generatedAddress?.address || generatedAddress

  // Fetch networks when crypto is selected
  useEffect(() => {
    const fetchNetworks = async () => {
      if (!selectedCryptoId) return
      setLoadingNetworks(true)
      try {
        const res = await CryptoAPI.getNetworksByCrypto(selectedCryptoId)
        if (res.success) {
          setNetworks(extractArray(res, 'networks') || res.data || [])
        }
      } catch (err) {
        console.error('Error fetching networks:', err)
      } finally {
        setLoadingNetworks(false)
      }
    }
    fetchNetworks()
  }, [selectedCryptoId])

  const handleGenerateAddress = async () => {
    if (!selectedNetwork) return
    const address = await onGenerateAddress(selectedCryptoId, selectedNetwork)
    if (address) {
      setGeneratedAddress(address)
      setStep(3)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleBack = () => {
    if (step === 2 && initialCrypto?.id) {
      // If we came with a preselected crypto, just close instead of going back
      onClose()
    } else {
      setStep(step - 1)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full md:w-[480px] bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button onClick={handleBack} className="p-2 hover:bg-slate-100 rounded-xl">
                  <ChevronRight className="w-5 h-5 text-slate-500 rotate-180" />
                </button>
              )}
              <h2 className="text-xl font-bold text-slate-900">Receive</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Step 1: Select Crypto */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-slate-500 mb-4">Select token to receive</p>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {currencies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setSelectedCryptoId(c.id); setStep(2); }}
                    className="w-full flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCryptoColor(c.symbol)} flex items-center justify-center`}>
                      <span className="font-bold text-white text-lg">{c.symbol?.charAt(0)}</span>
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-medium text-slate-900">{c.symbol}</p>
                      <p className="text-sm text-slate-500">{c.name}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Network */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCryptoColor(selectedCurrency?.symbol)} flex items-center justify-center`}>
                  <span className="font-bold text-white text-lg">{selectedCurrency?.symbol?.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedCurrency?.symbol}</p>
                  <p className="text-sm text-slate-500">Select network</p>
                </div>
              </div>

              {loadingNetworks ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              ) : networks.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500">No networks available for {selectedCurrency?.symbol}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {networks.map(n => (
                    <button
                      key={n.id}
                      onClick={() => {
                        setSelectedNetwork(n.id)
                        const existing = depositAddresses.find(a => a.network_id == n.id && a.crypto_id == selectedCryptoId)
                        if (existing) {
                          setStep(3)
                        }
                      }}
                      className={`w-full flex items-center justify-between p-4 border rounded-xl transition-colors ${
                        n.is_testnet
                          ? 'bg-amber-50 border-amber-300 hover:bg-amber-100'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          n.is_testnet ? 'bg-amber-200' : 'bg-slate-200'
                        }`}>
                          <Layers className={`w-5 h-5 ${n.is_testnet ? 'text-amber-700' : 'text-slate-600'}`} />
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{n.name}</p>
                            {n.is_testnet && (
                              <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded">
                                TESTNET
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{n.symbol || n.network_code}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </button>
                  ))}
                </div>
              )}

              {selectedNetwork && !depositAddresses.find(a => a.network_id == selectedNetwork && a.crypto_id == selectedCryptoId) && (
                <button
                  onClick={handleGenerateAddress}
                  disabled={processing}
                  className="w-full mt-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all disabled:opacity-50"
                >
                  {processing ? (
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  ) : (
                    'Generate Address'
                  )}
                </button>
              )}
            </div>
          )}

          {/* Step 3: Show Address */}
          {step === 3 && displayAddress && (() => {
            const selectedNet = networks.find(n => n.id == selectedNetwork);
            return (
            <div className="text-center">
              {selectedNet?.is_testnet && (
                <div className="mb-4 p-3 bg-amber-100 border border-amber-300 rounded-xl">
                  <p className="text-amber-800 font-semibold text-sm flex items-center justify-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    TESTNET - Only for testing, no real value!
                  </p>
                </div>
              )}
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${getCryptoColor(selectedCurrency?.symbol)} flex items-center justify-center mx-auto mb-4`}>
                <span className="font-bold text-white text-3xl">{selectedCurrency?.symbol?.charAt(0)}</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{selectedCurrency?.symbol} Address</h3>
              <div className="flex items-center justify-center gap-2 mb-6">
                <p className="text-slate-500 text-sm">{selectedNet?.name} Network</p>
                {selectedNet?.is_testnet && (
                  <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded">TESTNET</span>
                )}
              </div>

              {/* QR Code Placeholder */}
              <div className="w-48 h-48 bg-white border border-slate-200 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <QrCode className="w-32 h-32 text-slate-800" />
              </div>

              {/* Address */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
                <p className="text-slate-900 font-mono text-sm break-all">{displayAddress}</p>
              </div>

              <button
                onClick={() => copyToClipboard(displayAddress)}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copy Address
                  </>
                )}
              </button>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-500 text-sm text-left">
                    Only send {selectedCurrency?.symbol} on the {selectedNet?.name} network to this address.
                  </p>
                </div>
              </div>
            </div>
          )})()}
        </div>
      </motion.div>
    </motion.div>
  )
}

// Withdraw Modal - Swift Gold Fiduciary Style
function WithdrawModal({ currencies, holdings, onWithdraw, onClose, processing, getCryptoColor }) {
  const [selectedCrypto, setSelectedCrypto] = useState('')
  const [selectedNetwork, setSelectedNetwork] = useState('')
  const [amount, setAmount] = useState('')
  const [destinationAddress, setDestinationAddress] = useState('')
  const [error, setError] = useState('')
  const [step, setStep] = useState(1)
  const [networks, setNetworks] = useState([])
  const [loadingNetworks, setLoadingNetworks] = useState(false)

  const selectedHolding = holdings.find(h => h.id == selectedCrypto || h.crypto_id == selectedCrypto)
  const maxAmount = parseFloat(selectedHolding?.balance || selectedHolding?.holdings || 0)

  // Fetch networks when crypto is selected
  useEffect(() => {
    const fetchNetworks = async () => {
      if (!selectedCrypto) return
      setLoadingNetworks(true)
      try {
        const res = await CryptoAPI.getNetworksByCrypto(selectedCrypto)
        if (res.success) {
          setNetworks(extractArray(res, 'networks') || res.data || [])
        }
      } catch (err) {
        console.error('Error fetching networks:', err)
      } finally {
        setLoadingNetworks(false)
      }
    }
    fetchNetworks()
  }, [selectedCrypto])

  const handleSubmit = async () => {
    setError('')
    if (!destinationAddress) {
      setError('Please enter a destination address')
      return
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount')
      return
    }
    if (parseFloat(amount) > maxAmount) {
      setError('Insufficient balance')
      return
    }

    const result = await onWithdraw(selectedCrypto, selectedNetwork, parseFloat(amount), destinationAddress, '')
    if (!result?.success) {
      setError(result?.message || 'Transaction failed')
    }
  }

  const holdingsWithBalance = holdings.filter(h => (parseFloat(h.balance) || parseFloat(h.holdings) || 0) > 0)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full md:w-[480px] bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button onClick={() => setStep(step - 1)} className="p-2 hover:bg-slate-100 rounded-xl">
                  <ChevronRight className="w-5 h-5 text-slate-500 rotate-180" />
                </button>
              )}
              <h2 className="text-xl font-bold text-slate-900">Send</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Select Token */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-slate-500 mb-4">Select token to send</p>
              {holdingsWithBalance.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500">No tokens available to send</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {holdingsWithBalance.map(h => (
                    <button
                      key={h.id || h.crypto_id}
                      onClick={() => { setSelectedCrypto(h.id || h.crypto_id); setStep(2); }}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-xl transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCryptoColor(h.symbol)} flex items-center justify-center`}>
                          <span className="font-bold text-white text-lg">{h.symbol?.charAt(0)}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-900">{h.symbol}</p>
                          <p className="text-sm text-slate-500">
                            {parseFloat(h.balance || h.holdings || 0).toFixed(6)} available
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Enter Details */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCryptoColor(selectedHolding?.symbol)} flex items-center justify-center`}>
                  <span className="font-bold text-white text-lg">{selectedHolding?.symbol?.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedHolding?.symbol}</p>
                  <p className="text-sm text-slate-500">Available: {maxAmount.toFixed(6)}</p>
                </div>
              </div>

              {/* Network Selection */}
              <div>
                <label className="block text-slate-500 text-sm mb-2">Network</label>
                {loadingNetworks ? (
                  <div className="flex items-center justify-center py-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    <span className="ml-2 text-slate-500 text-sm">Loading networks...</span>
                  </div>
                ) : networks.length === 0 ? (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 text-sm text-center">
                    No networks available for this token
                  </div>
                ) : (
                  <select
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select network</option>
                    {networks.map(n => (
                      <option key={n.id} value={n.id}>{n.name} ({n.symbol || n.network_code})</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Destination Address */}
              <div>
                <label className="block text-slate-500 text-sm mb-2">Recipient Address</label>
                <div className="relative">
                  <input
                    type="text"
                    value={destinationAddress}
                    onChange={(e) => setDestinationAddress(e.target.value)}
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter address or scan QR"
                  />
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-100 rounded-lg">
                    <Scan className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-slate-500 text-sm mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    min="0"
                    max={maxAmount}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-white text-xl font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                  <button
                    onClick={() => setAmount(maxAmount.toString())}
                    className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-blue-600/20 text-[#3375BB] rounded-lg text-sm font-medium hover:bg-blue-600/30"
                  >
                    MAX
                  </button>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={processing || !selectedNetwork || !amount || !destinationAddress}
                className="w-full mt-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all disabled:opacity-50"
              >
                {processing ? (
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

// Swap Modal - Swift Gold Fiduciary Style
function SwapModal({ currencies, holdings, onClose, getCryptoColor }) {
  const [fromToken, setFromToken] = useState('')
  const [toToken, setToToken] = useState('')
  const [amount, setAmount] = useState('')

  const fromCurrency = holdings.find(h => h.id == fromToken || h.crypto_id == fromToken) || currencies.find(c => c.id == fromToken)
  const toCurrency = currencies.find(c => c.id == toToken)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full md:w-[480px] bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Swap</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="space-y-3">
            {/* From */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-sm">From</span>
                <span className="text-slate-500 text-sm">Balance: {fromCurrency?.holdings?.toFixed(4) || '0'}</span>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 bg-transparent text-2xl font-medium text-slate-900 placeholder-slate-400 focus:outline-none"
                  placeholder="0.0"
                />
                <select
                  value={fromToken}
                  onChange={(e) => setFromToken(e.target.value)}
                  className="bg-slate-100 text-slate-900 px-3 py-2 rounded-lg focus:outline-none border border-slate-200"
                >
                  <option value="">Select</option>
                  {holdings.filter(h => (parseFloat(h.balance) || parseFloat(h.holdings) || 0) > 0).map(h => (
                    <option key={h.id || h.crypto_id} value={h.id || h.crypto_id}>{h.symbol}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center -my-1">
              <button
                onClick={() => { const temp = fromToken; setFromToken(toToken); setToToken(temp); }}
                className="w-10 h-10 bg-[#363D47] rounded-xl flex items-center justify-center hover:bg-[#4A5568] transition-colors"
              >
                <ArrowDownLeft className="w-5 h-5 text-slate-500 rotate-45" />
              </button>
            </div>

            {/* To */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-sm">To</span>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  disabled
                  value={amount && fromCurrency && toCurrency ? (parseFloat(amount) * (parseFloat(fromCurrency.current_price) / parseFloat(toCurrency.current_price))).toFixed(6) : ''}
                  className="flex-1 bg-transparent text-2xl font-medium text-slate-900 placeholder-slate-400 focus:outline-none"
                  placeholder="0.0"
                />
                <select
                  value={toToken}
                  onChange={(e) => setToToken(e.target.value)}
                  className="bg-slate-100 text-slate-900 px-3 py-2 rounded-lg focus:outline-none border border-slate-200"
                >
                  <option value="">Select</option>
                  {currencies.filter(c => c.id != fromToken).map(c => (
                    <option key={c.id} value={c.id}>{c.symbol}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Rate Info */}
            {fromCurrency && toCurrency && (
              <div className="bg-slate-50 border border-slate-200/50 rounded-xl p-3 text-sm text-slate-500">
                <div className="flex justify-between">
                  <span>Rate</span>
                  <span>1 {fromCurrency.symbol} = {(parseFloat(fromCurrency.current_price) / parseFloat(toCurrency.current_price)).toFixed(6)} {toCurrency.symbol}</span>
                </div>
              </div>
            )}

            <button
              disabled={!fromToken || !toToken || !amount}
              className="w-full py-4 bg-[#F0B90B] hover:bg-[#D9A50A] text-[#0B0E11] rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Swap
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Stake Modal - Trust Wallet Style
function StakeModal({ pool, onClose, processing, getCryptoColor }) {
  const [amount, setAmount] = useState('')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full md:w-[480px] bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Stake {pool.symbol}</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Pool Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getCryptoColor(pool.symbol)} flex items-center justify-center`}>
                <span className="font-bold text-white text-xl">{pool.symbol?.charAt(0)}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{pool.name}</h3>
                <p className="text-slate-500 text-sm">{pool.description || 'Earn rewards by staking'}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{pool.apy_rate}%</p>
                <p className="text-xs text-slate-500">APY</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{pool.lock_period_days || 0}</p>
                <p className="text-xs text-slate-500">Lock Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{pool.min_stake || 0}</p>
                <p className="text-xs text-slate-500">Min Stake</p>
              </div>
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <label className="block text-slate-500 text-sm mb-2">Stake Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-white text-xl font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0ECB81]"
              placeholder="0.00"
            />
          </div>

          <button
            disabled={!amount || processing}
            className="w-full py-4 bg-[#0ECB81] hover:bg-[#0BA36A] text-white rounded-xl font-medium transition-all disabled:opacity-50"
          >
            {processing ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            ) : (
              'Stake Now'
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
